import { useAuthStore } from '~/stores/useAuthStore';
import { useSessionExpiredStore } from '~/stores/useSessionExpiredStore';
import { useMenuStore } from '~/stores/useMenuStore';
import { syncMenuWithSessionUser } from '~/composables/useLoginSuccess';
import { useCommonApi } from '~/composables/useCommonApi';
import { getActivePinia } from 'pinia';

// middleware/auth-check.global.ts
export default defineNuxtRouteMiddleware(async to => {
  // 모바일 페이지인 경우 미들웨어 건너뛰기
  if (to.path.startsWith('/mobile')) {
    return;
  }
  // 이중 슬래시만 처리하고 바로 리턴
  if (to.path.includes('//')) {
    const cleanPath = to.path.replace(/\/+/g, '/');
    return navigateTo(cleanPath, { replace: true });
  }

  // ⭐ Pinia가 아직 초기화되지 않았으면 스킵 (첫 로드 시)
  if (!getActivePinia()) {
    return;
  }

  const auth = useAuthStore();
  const menuStore = useMenuStore();

  // 인증이 필요하지 않은 페이지들 (public routes)
  const publicRoutes = ['/login', '/login.do', '/screens/logout'];
  const isPublicRoute = publicRoutes.includes(to.path);

  // 세션 체크 (public route가 아닌 경우 항상 수행)
  if (!isPublicRoute) {
    // checkAuth 전에 이미 로그인 상태였는지 확인 (세션 만료 vs 처음부터 미로그인 구분)
    const wasLoggedIn = auth.isLoggedIn;

    // 미들웨어에서는 자체 로직으로 다이얼로그 표시 여부 결정 (checkAuth의 자동 다이얼로그 비활성화)
    const isValid = await auth.checkAuth(false);
    if (!isValid) {
      if (wasLoggedIn && auth.lastCheckAuthReason === 'session') {
        const sessionExpiredStore = useSessionExpiredStore();
        if (!sessionExpiredStore.isVisible) {
          sessionExpiredStore.show();
        }
        return;
      }
      if (wasLoggedIn && auth.lastCheckAuthReason === 'network') {
        // 통신 오류: 세션 만료로 간주하지 않고 현재 페이지 유지
        return;
      }
      if (!wasLoggedIn) {
        return navigateTo('/login');
      }
      return;
    }
  }

  // 로그인 상태이면 세션 사용자 기준으로 메뉴 동기화
  // (타사용자 SSO 로그인 직후 stale 관리자 메뉴 캐시 교체 포함)
  if (auth.isLoggedIn && !isPublicRoute) {
    // 구독 서비스는 탭 게이팅에 쓰이므로 메뉴를 만들기 전에 확보해야 한다.
    // 로그인 화면을 거치지 않은 세션(새로고침, 로그인 이후 배포된 버전)은 캐시가 없으므로
    // 여기서 한 번 조회한다. 안 하면 requiredServices 가 걸린 탭이 통째로 사라진다.
    menuStore.restoreServiceTypes();
    if (menuStore.serviceTypes.length === 0) {
      try {
        const { fetchServiceTypeList } = useCommonApi();
        const res: any = await fetchServiceTypeList();
        menuStore.setServiceTypes(res?.serviceTypeList || []);
      } catch (error) {
        console.error('구독 서비스 목록 조회 실패:', error);
      }
    }
    await syncMenuWithSessionUser();
  }

  // /login.do 처리 (SPA 환경 호환)
  if (to.path === '/login.do') {
    if (auth.isLoggedIn) {
      if (process.client) {
        sessionStorage.setItem('sgate:showLoginPopNotice', 'Y');
      }
      // 로그인되어 있으면 메인 페이지로
      return navigateTo(auth.user?.mainPgmPath);
    } else {
      // 로그인 안 되어 있으면 로그인 화면
      return navigateTo('/login');
    }
  }

  // 루트 경로 처리를 가장 먼저
  if (to.path === '/') {
    if (auth.isLoggedIn) {
      return navigateTo(auth.user?.mainPgmPath);
    } else {
      return navigateTo('/login');
    }
  }

  // 인증되지 않은 사용자가 보호된 페이지에 접근하려고 할 때
  if (!auth.isLoggedIn && !isPublicRoute) {
    return navigateTo('/login');
  }

  // 이미 로그인된 사용자가 로그인 페이지에 접근하려고 할 때
  if (auth.isLoggedIn && to.path === '/login') {
    return navigateTo(auth.user?.mainPgmPath);
  }
});
